import { reactive, shallowRef, toRefs } from 'vue';
import mapboxgl from 'mapbox-gl';
import { taxiStartingFees, taxiPerKmRates } from '@/data/taxis.js';
//import { apiKey as ORS_API_KEY, ORS_BASE_URL } from '@/constants/ors.js';
import { useGameStore } from '@/stores/useGameStore.js';
import { countryCodeFromLatLng } from '@/utils/geolocation.js';
import { notify } from '@kyvg/vue3-notification';
import { validRoute } from '@/utils/gameplay.js';

/**
 * Composable for planning and confirming routes (walk or taxi) using OpenRouteService
 * @param {object} mapRef         - Mapbox GL map instance
 * @param {object} personMarkerRef- The Vue-ref pointing to the person marker instance
 * @param {Function} logGame   - Action to record route events
 * @param {Function} deductMoney - Action to charge taxi fares
 * @param {Function} moveMarkerAlongRoute - Starts the in-game animation
 */
export function useRouting(mapRef, personMarkerRef, logGame, deductMoney, moveMarkerAlongRoute, canPlan = () => true) {
  let requestId = 0;
  const routePlan = reactive({
    route: null,         // GeoJSON LineString
    mode: null,          // 'walk' | 'taxi'
    travelTime: null,    // seconds
    distance: null,      // meters
    cost: null,          // GBP
    currency_code: null,      // 'GBP' | 'EUR'
    currency_symbol: null,    // '£' | '€'
    destination: null,   // { lat, lng }
    confirmed: false,
    routing: false
  });

  const destinationMarker = shallowRef(null);

  function clearDestinationMarker() {
    // (Optional: you might want to disable clicks if the user is already planning a route.)
    // Remove any existing destination marker and planned route.
    if (destinationMarker.value) {
      destinationMarker.value.remove();
      destinationMarker.value = null;

      //routePlan.route = null;
      //routePlan.travelTime = null;
      //routePlan.mode = null;
      //routePlan.destination = null;
      removeRouteFromMap();
    }
  }

  function addDestinationMarker(destination) {
    const map = mapRef.value;
    if (!map) return;

    // remove any old one
    clearDestinationMarker();

    const popupDiv = document.createElement("div");
    const walkButton = document.createElement("button");
    walkButton.textContent = "Walk here";
    walkButton.style.marginRight = "5px";
    const taxiButton = document.createElement("button");
    taxiButton.textContent = "Taxi here";
    popupDiv.appendChild(walkButton);
    popupDiv.appendChild(taxiButton);

    // create the new marker
    const m = new mapboxgl.Marker({ color: 'green' })
      .setLngLat([destination.lng, destination.lat])
      .setPopup(new mapboxgl.Popup().setDOMContent(popupDiv))
      .addTo(map);
    m.togglePopup();

    walkButton.addEventListener("click", async (event) => {
      event.stopPropagation();
      console.log("Planning walking route to:", destination);
      await planRoute("walk", destination);
    });
    taxiButton.addEventListener("click", async (event) => {
      event.stopPropagation();
      await planRoute("taxi", destination);
    });

    destinationMarker.value = m;
  }

  // Internal helpers to manipulate map layers
  function displayRouteOnMap(routeGeoJSON) {
    const map = mapRef.value;
    if (map.getSource('route')) {
      map.getSource('route').setData(routeGeoJSON);
    } else {
      map.addSource('route', { type: 'geojson', data: routeGeoJSON });
      map.addLayer({
        id: 'route', type: 'line', source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#888', 'line-width': 6 },
      });
    }
  }

  function removeRouteFromMap() {
    const map = mapRef.value;
    if (!map) return;
    if (map.getLayer('route')) map.removeLayer('route');
    if (map.getSource('route')) map.removeSource('route');
  }

  function addConfirmedRouteLayers() {
    const map = mapRef.value;
    if (!map) return;

    if (!map.getSource('route-completed')) {
      map.addSource('route-completed', {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [] } }
      });
      map.addLayer({
        id:     'route-completed',
        type:   'line',
        source: 'route-completed',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint:  { 'line-color': '#00FF00', 'line-width': 6 }
      });
    }

    if (!map.getSource('route-remaining')) {
      map.addSource('route-remaining', {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [] } }
      });
      map.addLayer({
        id:     'route-remaining',
        type:   'line',
        source: 'route-remaining',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint:  {
          'line-color':   '#FF0000',
          'line-width':    6,
          'line-dasharray': [2,2]
        }
      });
    }
  }

  /**
   * Plan a new route from the person's current location to `destination`.
   * @param {'walk'|'taxi'} mode
   * @param {{ lat: number, lng: number }} destination
   */
  async function planRouteMapbox(mode, destination) {
    if (!mapRef.value || !personMarkerRef.value) return;

    routePlan.routing = true;

    const map    = mapRef.value;
    const marker = personMarkerRef.value;
    const start  = marker.getLngLat();
    const profile = mode === 'walk' ? 'walking' : 'driving';

    // Use Mapbox Directions API. Use the mapboxgl.accessToken already configured for the map.
    const token = mapboxgl.accessToken || '';
    if (!token) {
      console.error('Mapbox access token not found (mapboxgl.accessToken)');
      routePlan.routing = false;
      return;
    }

    // Mapbox expects lon,lat pairs
    const coordinates = `${start.lng},${start.lat};${destination.lng},${destination.lat}`;
    const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${coordinates}?geometries=polyline&overview=full&alternatives=false&access_token=${token}`;

    // Small polyline v1 decoder to convert encoded polyline to coordinates array
    function decodePolyline(str) {
      let index = 0, lat = 0, lng = 0, coordinates = [];
      while (index < str.length) {
        let b, shift = 0, result = 0;
        do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
        const deltaLat = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lat += deltaLat;

        shift = 0; result = 0;
        do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
        const deltaLng = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lng += deltaLng;

        coordinates.push([lng / 1e5, lat / 1e5]);
      }
      return coordinates;
    }

    try {
      const res = await fetch(url);
      const json = await res.json();
      // Mapbox returns routes array
      const route = json.routes?.[0];
      if (!route) {
        console.error('No route returned from Mapbox', json);
        routePlan.routing = false;
        return;
      }

      // decode polyline geometry into GeoJSON LineString
      const decoded = decodePolyline(route.geometry);
      const geojson = { type: 'Feature', geometry: { type: 'LineString', coordinates: decoded } };

      // populate reactive plan
      routePlan.route      = geojson;
      routePlan.travelTime = route.duration; // seconds
      routePlan.distance   = route.distance; // meters
      routePlan.mode       = mode;
      routePlan.destination= destination;
      routePlan.confirmed  = false;

      // Get the country name the user is in, to compute taxi cost


      // compute taxi cost if needed
      if (mode === 'taxi') {
        const gameStore = useGameStore();
        let userCountry = gameStore.currentCountryCode ?? 'UK';
        userCountry = userCountry === 'UK' ? 'GB' : userCountry;

        const fee   = taxiStartingFees[userCountry] || 3.72;
        const perKm = taxiPerKmRates[userCountry] || 1.54;
        routePlan.cost = Math.round((fee + (routePlan.distance/1000)*perKm)*100)/100;

        routePlan.currency_code = (userCountry === 'GB' ? 'GBP' : 'EUR');
        routePlan.currency_symbol = (userCountry === 'GB' ? '£' : '€');
      } else {
        routePlan.cost = null;
        routePlan.currency_code = null;
        routePlan.currency_symbol = null;
      }

      routePlan.routing = false;
      displayRouteOnMap(routePlan.route);
    } catch (err) {
      console.error('Route planning failed', err);
      routePlan.routing = false;
    }
  }

  async function planRoute(mode, destination) {
    if (!mapRef.value || !personMarkerRef.value || !canPlan()) return;

    const id = ++requestId;
    routePlan.route = null;
    routePlan.confirmed = false;
    routePlan.routing = true;
    removeRouteFromMap();

    const map    = mapRef.value;
    const marker = personMarkerRef.value;
    const start  = marker.getLngLat();
    const profile = mode === 'walk' ? 'foot-hiking' : 'driving-car';
    const params = new URLSearchParams({
      profile,
      start_lng: start.lng,
      start_lat: start.lat,
      end_lng: destination.lng,
      end_lat: destination.lat,
    });
    const url = `${baseUrl}api/directions?${params}`;

    try {
      const res = await fetch(url);
      const json = await res.json();
      const feat = json.features?.[0];
      if (id !== requestId) return;
      if (!res.ok || !validRoute(feat) || !(Number(feat.properties?.segments?.[0]?.duration) > 0)) {
        throw new Error('No usable route was returned. Try a nearby road or another destination.');
      }

      // populate reactive plan
      routePlan.route      = { type: 'Feature', geometry: feat.geometry };
      const seg             = feat.properties.segments[0];
      routePlan.travelTime = seg.duration;
      routePlan.distance   = seg.distance;
      routePlan.mode       = mode;
      routePlan.destination= destination;
      routePlan.confirmed  = false;

      // Get the country name the user is in, to compute taxi cost


      // compute taxi cost if needed
      if (mode === 'taxi') {
        const gameStore = useGameStore();
        let userCountry = countryCodeFromLatLng(start.lat, start.lng) || gameStore.currentCountryCode || 'GB';
        userCountry = userCountry === 'UK' ? 'GB' : userCountry;

        const fee   = taxiStartingFees[userCountry] || 3.72;
        const perKm = taxiPerKmRates[userCountry] || 1.54;
        routePlan.cost = Math.round((fee + (routePlan.distance/1000)*perKm)*100)/100;

        routePlan.currency_code = (userCountry === 'GB' ? 'GBP' : 'EUR');
        routePlan.currency_symbol = (userCountry === 'GB' ? '£' : '€');
      } else {
        routePlan.cost = null;
        routePlan.currency_code = null;
        routePlan.currency_symbol = null;
      }

      routePlan.routing = false;
      displayRouteOnMap(routePlan.route);
    } catch (err) {
      console.error('Route planning failed', err);
      if (id === requestId) notify({ title: 'Route unavailable', text: 'Could not find a route. Try a nearby road or another destination.', type: 'error' });
    } finally {
      if (id === requestId) routePlan.routing = false;
    }
  }

  /** Confirm and animate the current route */
  function confirmRoute() {
    if (!canPlan() || routePlan.confirmed || routePlan.routing || !validRoute(routePlan.route) || !routePlan.travelTime) return;

    clearDestinationMarker();
    removeRouteFromMap();

    if (routePlan.mode === 'taxi') {
      const result = deductMoney(routePlan.cost, routePlan.currency_code);
      if(!result) {
        cancelRoute();
        return;
      }
    }

    logGame({
      event: routePlan.mode.charAt(0).toUpperCase() + routePlan.mode.slice(1),
      from_lat: personMarkerRef.value.getLngLat().lat,
      from_lng: personMarkerRef.value.getLngLat().lng,
      to_lat: routePlan.destination.lat,
      to_lng: routePlan.destination.lng,
      end: routePlan.travelTime,
      money: routePlan.cost || 0,
      currency: routePlan.currency_code,
    });

    addConfirmedRouteLayers();

    moveMarkerAlongRoute(personMarkerRef.value, routePlan.route, routePlan.travelTime, [], mapRef.value, routePlan.mode);
    routePlan.confirmed = true;

    const gameStore = useGameStore();
    gameStore.incrementJourneyCount();
  }

  /** Cancel any in-progress plan */
  function cancelRoute() {
    requestId++;
    clearDestinationMarker();
    removeRouteFromMap();

    Object.assign(routePlan, {
      route: null, distance: null, travelTime: null,
      cost: null, mode: null, destination: null, confirmed: false, routing: false,
    });
  }

  return {
    ...toRefs(routePlan),
    addDestinationMarker,
    addConfirmedRouteLayers,
    planRoute,
    confirmRoute,
    cancelRoute,
    
  };
}
